import Cookies from 'js-cookie';

export const CART_STORAGE_KEY = 'jamrock_cart';

const cookieOptions = {
  expires: 7,
  sameSite: 'Lax',
  secure: process.env.NODE_ENV === 'production',
  path: '/',
};

export const normalizeObject = (obj) => {
  if (!obj) return '';
  const keys = Object.keys(obj).sort();
  const sorted = {};
  keys.forEach((k) => { sorted[k] = obj[k]; });
  return JSON.stringify(sorted);
};

export const generateLineId = (item) => {
  const baseId = item.id ?? item.product_id ?? 'unknown';
  const blister = item.blister_id ?? '';
  const variantsSig = normalizeObject(item.variants);
  return `${baseId}::${blister}::${variantsSig}`;
};

const normalizeCartItem = (item) => ({
  ...item,
  // Recalcular siempre para migrar IDs antiguos/inconsistentes.
  lineId: generateLineId(item),
});

const normalizeStoredCart = (items) => {
  const uniqueLines = new Map();

  for (const rawItem of Array.isArray(items) ? items : []) {
    const item = normalizeCartItem(rawItem);
    const existing = uniqueLines.get(item.lineId);
    if (!existing) {
      uniqueLines.set(item.lineId, item);
    } else {
      // Los duplicados físicos antiguos representan una misma línea. Conservar
      // la mayor cantidad evita duplicar unidades durante la migración.
      existing.quantity = Math.max(Number(existing.quantity) || 1, Number(item.quantity) || 1);
    }
  }

  return Array.from(uniqueLines.values());
};

export const loadCartFromStorage = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (raw) {
      return normalizeStoredCart(JSON.parse(raw));
    }

    // Migrar carrito existente de cookie a localStorage (una sola vez)
    const cookieRaw = Cookies.get('cart');
    if (cookieRaw) {
      const migrated = normalizeStoredCart(JSON.parse(cookieRaw));
      persistCart(migrated);
      Cookies.remove('cart', cookieOptions);
      return migrated;
    }

    return [];
  } catch {
    return [];
  }
};

export const persistCart = (cart) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
};

export const clearCartStorage = () => {
  localStorage.removeItem(CART_STORAGE_KEY);
  Cookies.remove('cart', cookieOptions);
};

/**
 * Combina dos carritos: conserva todos los ítems únicos por lineId.
 * Para líneas duplicadas, toma la cantidad mayor (evita pérdidas entre pestañas).
 */
export const mergeCartLines = (cartA, cartB) => {
  const map = new Map();

  for (const item of [...cartA, ...cartB]) {
    const normalized = normalizeCartItem(item);
    const existing = map.get(normalized.lineId);
    if (existing) {
      existing.quantity = Math.max(existing.quantity, normalized.quantity);
    } else {
      map.set(normalized.lineId, { ...normalized });
    }
  }

  return Array.from(map.values());
};

/**
 * Añade ítems del carrito remoto que no existen en el local (recuperación cross-tab).
 */
export const mergeRemoteCart = (localCart, remoteCart) => {
  const map = new Map(localCart.map((item) => [item.lineId, { ...item }]));

  for (const item of remoteCart) {
    const normalized = normalizeCartItem(item);
    const existing = map.get(normalized.lineId);
    if (!existing) {
      map.set(normalized.lineId, { ...normalized });
    } else if (normalized.quantity > existing.quantity) {
      existing.quantity = normalized.quantity;
    }
  }

  return Array.from(map.values());
};
